import React, { useState } from "react";
import { ComplianceChecker } from "../common/SharedComponents";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";
const G = "#0077B5";
const GRN = "#1D9E75";
const AMBER = "#D97706";
const BORDER = "#e0dfdc";

function fmtNum(n) {
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return String(n || 0);
}

/* ─── Section accordion wrapper ─── */
function Section({ title, count, countColor, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 16 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: "100%", background: "transparent", border: "none", padding: "10px 0", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontFamily: "inherit" }}
      >
        <span style={{ fontSize: 15, fontWeight: 600, color: "#191919" }}>{title}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {count != null && (
            <span style={{ fontSize: 12, fontWeight: 500, color: countColor || "#888", background: countColor ? (countColor === GRN ? "#E1F5EE" : "#FFFBEB") : "#f3f2ef", padding: "2px 10px", borderRadius: 12 }}>
              {count}
            </span>
          )}
          <span style={{ fontSize: 12, color: "#888", transition: "transform 0.2s", display: "inline-block", transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}>▾</span>
        </span>
      </button>
      <div style={{ maxHeight: open ? "none" : 0, overflow: "hidden", transition: "max-height 0.25s ease" }}>
        {open && children}
      </div>
    </div>
  );
}

/* ─── Full post card (for Needs Approval) ─── */
function FullPostCard({
  post, user, isAutopilot,
  editingCard, setEditingCard, editSaving, saveCaption,
  postEditComplianceResult, setPostEditComplianceResult,
  swapPanelPostId, setSwapPanelPostId, swapAlternatives, swapLoading,
  swapJustDone, setSwapJustDone, swappingPostId,
  rewriteUsage, openSwapPanel, doSwapArticle, saveSwapLinkStyle,
  handleApprovePost, handleDashRewrite,
  reschedulingCard, setReschedulingCard,
  deletingPost, setDeletingPost, setThisWeek, setToast,
  menuOpen, setMenuOpen, approvingId, setApprovingId,
  impressionsPerPost, isPlug,
}) {
  const isPending = post.status === "pending" || post.status === "rate_limited";
  const isApproved = post.status === "approved" || post.status === "scheduled";
  const isPosted = post.status === "posted";
  const isExpired = post.status === "expired";
  const isPaused = post.status === "paused";
  const isBlocked = post.status === "compliance_blocked" || post.status === "compliance_failed";
  const plug = isPlug(post);
  const schedLabel = post.scheduled_time
    ? new Date(post.scheduled_time).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : post.day || "";

  return (
    <div style={{
      background: "#fff",
      border: `1px solid ${BORDER}`,
      borderLeft: plug ? `3px solid ${AMBER}` : isApproved ? `3px solid ${GRN}` : isExpired ? `3px solid #DC2626` : `1px solid ${BORDER}`,
      borderRadius: 12,
      padding: "16px 20px",
      opacity: isPosted || isExpired ? 0.7 : isApproved ? 0.85 : 1,
      transition: "opacity 0.2s",
    }}>
      {/* Header line */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, fontSize: 12, color: "#888" }}>
        {isPending && <span>{plug ? "📌 Your weekly plug" : (user?.linkedin_name || "You")} · {schedLabel}</span>}
        {isApproved && <span style={{ color: GRN, fontWeight: 600 }}>✓ Approved · Goes out {schedLabel}</span>}
        {isPosted && <span>● Posted · {schedLabel}</span>}
        {isExpired && <span style={{ color: "#DC2626", fontWeight: 600 }}>Expired — not approved in time</span>}
        {isPaused && <span style={{ color: "#92400E" }}>Paused · {schedLabel}</span>}
        {isBlocked && <span style={{ color: "#DC2626", fontWeight: 600 }}>Blocked by compliance check — edit and resubmit</span>}
      </div>

      {/* Content source label — distinguishes curated vs in-house */}
      {!plug && (
        <div style={{ fontSize: 11, marginBottom: 6 }}>
          {post.content_source === "curated_rss"
            ? <span style={{ color: "#0F6E56", fontWeight: 500 }}>Curated for your practice</span>
            : <span style={{ color: "#888" }}>BrandHives content · adapted to your voice</span>}
        </div>
      )}

      {/* Caption */}
      <div style={{ fontSize: 14, color: "#191919", lineHeight: 1.55, marginBottom: 10, whiteSpace: "pre-wrap" }}>
        {post.caption || ""}
      </div>

      {/* Article preview */}
      {post.article_url && post.article_title && (
        <div style={{ background: "#f8f8f6", border: `1px solid #e8e8e4`, borderRadius: 8, padding: "10px 12px", marginBottom: 12, display: "flex", gap: 10, alignItems: "center" }}>
          {post.image_url && <img src={post.image_url} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} onError={e => { e.target.style.display = "none"; }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#191919", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{post.article_title}</div>
            <div style={{ fontSize: 11, color: "#888" }}>{(post.article_url || "").replace(/^https?:\/\//, "").split("/")[0]}</div>
          </div>
        </div>
      )}

      {/* Edit panel (inline) */}
      {editingCard?.id === post.id && (
        <div style={{ marginBottom: 12 }}>
          <textarea value={editingCard.caption} onChange={e => { setEditingCard(c => ({ ...c, caption: e.target.value })); setPostEditComplianceResult(null); }} rows={6}
            style={{ width: "100%", border: `1px solid ${BORDER}`, borderRadius: 8, padding: 12, fontSize: 14, lineHeight: 1.55, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
          <ComplianceChecker caption={editingCard.caption} plugPostId={null} API_BASE={API_BASE} onCaptionFixed={cap => setEditingCard(c => ({ ...c, caption: cap }))} onResult={setPostEditComplianceResult} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button onClick={() => { setEditingCard(null); setPostEditComplianceResult(null); }} style={{ background: "transparent", color: "#666", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
            <button onClick={() => saveCaption(post.id, editingCard.caption)} disabled={editSaving || postEditComplianceResult?.has_blocks}
              style={{ background: (editSaving || postEditComplianceResult?.has_blocks) ? "#ccc" : G, color: "#fff", border: "none", borderRadius: 6, padding: "6px 16px", fontSize: 13, fontWeight: 600, cursor: (editSaving || postEditComplianceResult?.has_blocks) ? "not-allowed" : "pointer" }}>
              {editSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* Swap panel */}
      {swapPanelPostId === post.id && (
        <div style={{ background: "#F0F7FF", border: "1px solid #B3D4E8", borderRadius: 10, padding: 16, marginBottom: 12 }}>
          {swapJustDone?.postId === post.id ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#005885", marginBottom: 10 }}>Article swapped successfully.</div>
              <button onClick={() => { setSwapJustDone(null); setSwapPanelPostId(null); }}
                style={{ background: G, color: "#fff", border: "none", borderRadius: 7, padding: "7px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Done
              </button>
            </>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#005885" }}>Pick a replacement:</span>
                <span style={{ fontSize: 11, color: "#888" }}>{rewriteUsage?.remaining ?? "?"} swaps left</span>
              </div>
              {swapLoading ? (
                <div style={{ fontSize: 13, color: "#666" }}>Finding alternatives…</div>
              ) : swapAlternatives.length === 0 ? (
                <div style={{ fontSize: 13, color: "#666" }}>
                  No alternatives found.
                  <button onClick={() => setSwapPanelPostId(null)} style={{ marginLeft: 8, background: "transparent", color: "#666", border: "1px solid #d0dbe5", borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>Keep original</button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {swappingPostId === post.id && (
                    <div style={{ fontSize: 13, color: "#005885", display: "flex", alignItems: "center", gap: 6 }}>
                      <span className="spin" style={{ display: "inline-block" }}>◌</span> Swapping and rewriting…
                    </div>
                  )}
                  {swapAlternatives.map(alt => (
                    <div key={alt.id} style={{ background: "#fff", border: "1px solid #D0E8F5", borderRadius: 8, padding: "10px 14px", display: "flex", gap: 10, alignItems: "center", opacity: swappingPostId === post.id ? 0.5 : 1 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A", lineHeight: 1.4 }}>{alt.title}</div>
                        <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{alt.category}</div>
                      </div>
                      <button onClick={() => doSwapArticle(post.id, alt.article_url)} disabled={swappingPostId === post.id}
                        style={{ background: swappingPostId === post.id ? "#6baed6" : G, color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: swappingPostId === post.id ? "not-allowed" : "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
                        {swappingPostId === post.id ? "…" : "Use this one →"}
                      </button>
                    </div>
                  ))}
                  <button onClick={() => setSwapPanelPostId(null)} style={{ background: "transparent", color: "#666", border: "none", padding: 0, fontSize: 12, cursor: "pointer", textAlign: "left" }}>Keep original</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Action bar */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 4 }}>
        {isPending && !isAutopilot && (
          <>
            <button onClick={() => { if (swapPanelPostId === post.id) { setSwapPanelPostId(null); setSwapJustDone(null); } else { openSwapPanel(post.id); } }}
              data-testid={`btn-post-swap-${post.id}`}
              style={{ background: "transparent", color: "#666", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}>
              Not this one ↻
            </button>
            <button
              onClick={async () => { setApprovingId(post.id); await handleApprovePost(post.id); setApprovingId(null); }}
              disabled={approvingId === post.id}
              data-testid={`btn-post-approve-${post.id}`}
              style={{ background: approvingId === post.id ? "#aaa" : GRN, color: "#fff", border: "none", borderRadius: 6, padding: "7px 18px", fontSize: 13, fontWeight: 600, cursor: approvingId === post.id ? "not-allowed" : "pointer", transition: "background 0.2s" }}>
              {approvingId === post.id ? "Approving…" : "Approve ✓"}
            </button>
            <div style={{ position: "relative" }}>
              <button onClick={() => setMenuOpen(menuOpen === post.id ? null : post.id)}
                data-testid={`btn-post-menu-${post.id}`}
                style={{ background: "transparent", color: "#888", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "7px 10px", fontSize: 16, cursor: "pointer", lineHeight: 1 }}>
                ···
              </button>
              {menuOpen === post.id && (
                <div style={{ position: "absolute", right: 0, top: 36, background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 50, minWidth: 160, overflow: "hidden" }}
                  onMouseLeave={() => setMenuOpen(null)}>
                  <button onClick={() => { setMenuOpen(null); setEditingCard({ id: post.id, caption: post.caption || "" }); }}
                    style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "10px 16px", fontSize: 13, color: "#333", cursor: "pointer" }}>Edit text</button>
                  <button onClick={() => { setMenuOpen(null); handleDashRewrite(post); }}
                    style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "10px 16px", fontSize: 13, color: "#333", cursor: "pointer" }}>Rewrite in my voice</button>
                  <button onClick={() => { setMenuOpen(null); setReschedulingCard({ id: post.id, time: post.scheduled_time || "" }); }}
                    style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "10px 16px", fontSize: 13, color: "#333", cursor: "pointer" }}>Reschedule</button>
                  <button onClick={async () => {
                    setMenuOpen(null);
                    if (!window.confirm("Delete this post?")) return;
                    setDeletingPost(post.id);
                    await fetch(`${API_BASE}/app/api/user/scheduled-posts/${post.id}`, { method: "DELETE", credentials: "include" });
                    setThisWeek(w => w.filter(p => p.id !== post.id));
                    setDeletingPost(null);
                  }}
                    style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "10px 16px", fontSize: 13, color: "#dc3545", cursor: "pointer" }}>Delete</button>
                </div>
              )}
            </div>
          </>
        )}

        {isApproved && (
          <>
            <span style={{ background: "#E1F5EE", color: GRN, border: `1px solid ${GRN}44`, borderRadius: 6, padding: "5px 14px", fontSize: 12, fontWeight: 600 }}>Approved ✓</span>
            <button onClick={() => setEditingCard({ id: post.id, caption: post.caption || "" })}
              data-testid={`btn-post-edit-approved-${post.id}`}
              style={{ background: "transparent", color: "#888", border: "none", padding: 0, fontSize: 12, cursor: "pointer" }}>
              Edit before post
            </button>
          </>
        )}

        {isPosted && (
          <span style={{ fontSize: 12, color: "#888" }}>Est. reach: ~{fmtNum(impressionsPerPost)} impressions</span>
        )}
      </div>

      {/* Reschedule panel */}
      {reschedulingCard?.id === post.id && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
          <input type="datetime-local" value={reschedulingCard.time ? new Date(reschedulingCard.time).toISOString().slice(0,16) : ""}
            onChange={e => setReschedulingCard(c => ({ ...c, time: e.target.value }))}
            style={{ flex: 1, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "6px 12px", fontSize: 13, outline: "none", minWidth: 200 }} />
          <button onClick={async () => {
            if (!reschedulingCard.time) return;
            const r = await fetch(`${API_BASE}/app/api/user/scheduled-posts/${post.id}/reschedule`, {
              method: "PATCH", credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ scheduled_time: new Date(reschedulingCard.time).toISOString() }),
            });
            const d = await r.json();
            if (d.success) {
              setThisWeek(w => w.map(p => p.id === post.id ? { ...p, scheduled_time: d.scheduled_time } : p));
              setReschedulingCard(null);
              setToast("Rescheduled");
              setTimeout(() => setToast(null), 3000);
            }
          }} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Save time
          </button>
          <button onClick={() => setReschedulingCard(null)} style={{ background: "transparent", color: "#666", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
        </div>
      )}
    </div>
  );
}

/* ─── Compact post row (for Upcoming / Published) ─── */
function CompactPostRow({ post, impressionsPerPost, showStatus, onClick }) {
  const date = post.scheduled_time || post.scheduled_for || post.posted_at || "";
  const dateLabel = date ? new Date(date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "";
  const caption = (post.caption || "").substring(0, 80) + ((post.caption || "").length > 80 ? "…" : "");
  const isLive = post.status === "posted";
  const isApproved = post.status === "approved" || post.status === "scheduled";

  return (
    <div
      onClick={onClick}
      style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, cursor: onClick ? "pointer" : "default" }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 12, color: "#888", flexShrink: 0 }}>{dateLabel}</span>
          {showStatus && (
            <span style={{ fontSize: 11, fontWeight: 600, color: isLive ? GRN : isApproved ? GRN : AMBER, background: isLive ? "#E1F5EE" : isApproved ? "#E1F5EE" : "#FFFBEB", padding: "1px 8px", borderRadius: 10 }}>
              {isLive ? "Live" : isApproved ? "Approved" : "Pending"}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{caption}</div>
      </div>
      {isLive && (
        <div style={{ display: "flex", gap: 12, flexShrink: 0, fontSize: 12, color: "#888" }}>
          <span>{fmtNum(impressionsPerPost)} views</span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN POSTS PAGE
   ═══════════════════════════════════════════════════════════════════════════ */

export default function PostsPage({
  user, thisWeek, setThisWeek, pastPosts, postsLoading,
  editingCard, setEditingCard, cardRewriting,
  reschedulingCard, setReschedulingCard, deletingPost, setDeletingPost,
  swapPanelPostId, setSwapPanelPostId, swapAlternatives, swapLoading,
  swapJustDone, setSwapJustDone, swappingPostId,
  postEditComplianceResult, setPostEditComplianceResult,
  rewriteUsage, pendingCount,
  openSwapPanel, doSwapArticle, saveSwapLinkStyle,
  handleApprovePost, handleDashRewrite,
  postingMode, handlePostingMode,
  onNavigateSettings, setToast, showPastPosts, setShowPastPosts,
  onShowComposer, dashState, reviewDeadline,
}) {
  const [editSaving, setEditSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [expandedUpcoming, setExpandedUpcoming] = useState(null);
  const [publishedPeriod, setPublishedPeriod] = useState("this_week");
  const followerCount = user?.follower_count || 500;
  const impressionsPerPost = Math.max(1, Math.round(followerCount * 0.0022));

  const saveCaption = async (postId, caption) => {
    setEditSaving(true);
    try {
      await fetch(`${API_BASE}/app/api/user/scheduled-posts/${postId}`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ caption }) });
      setThisWeek(w => w.map(p => p.id === postId ? { ...p, caption } : p));
      setEditingCard(null);
    } catch {}
    setEditSaving(false);
  };

  const isPlug = (post) => post.is_plug || post.post_type === "plug";
  const canCreatePost = user?.plan && user.plan !== "free";
  const isAutopilot = postingMode === "autopilot";

  // Date helpers — week starts Monday (getDay: 0=Sun, 1=Mon, ..., 6=Sat)
  const now = new Date();
  const weekStart = new Date(now);
  const dayOfWeek = now.getDay(); // 0=Sun
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  weekStart.setDate(now.getDate() - daysSinceMonday);
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  const twoWeeksOut = new Date(weekEnd);
  twoWeeksOut.setDate(twoWeeksOut.getDate() + 14);
  const fmtDate = d => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const weekEndFri = new Date(weekStart);
  weekEndFri.setDate(weekStart.getDate() + 4);
  const weekLabel = `This week — ${fmtDate(weekStart)}-${fmtDate(weekEndFri)}`;

  // Split thisWeek into needs approval vs upcoming
  const needsApproval = thisWeek.filter(p => {
    const st = p.scheduled_time || p.scheduled_for;
    const d = st ? new Date(st) : null;
    const inThisWeek = d ? d >= weekStart && d < weekEnd : true;
    return (p.status === "pending" || p.status === "rate_limited") && inThisWeek;
  });

  const approvedThisWeek = thisWeek.filter(p => {
    const st = p.scheduled_time || p.scheduled_for;
    const d = st ? new Date(st) : null;
    const inThisWeek = d ? d >= weekStart && d < weekEnd : true;
    return (p.status === "approved" || p.status === "scheduled") && inThisWeek;
  });

  const terminalThisWeek = thisWeek.filter(p => {
    const st = p.scheduled_time || p.scheduled_for;
    const d = st ? new Date(st) : null;
    const inThisWeek = d ? d >= weekStart && d < weekEnd : true;
    return (p.status === "expired" || p.status === "paused" || p.status === "compliance_blocked" || p.status === "compliance_failed") && inThisWeek;
  });

  const upcoming = thisWeek.filter(p => {
    const st = p.scheduled_time || p.scheduled_for;
    if (!st) return false;
    const d = new Date(st);
    return d >= weekEnd && (p.status === "pending" || p.status === "approved" || p.status === "scheduled" || p.status === "rate_limited");
  }).sort((a, b) => new Date(a.scheduled_time || a.scheduled_for) - new Date(b.scheduled_time || b.scheduled_for));

  // All in needs approval section: pending first, then approved
  const needsApprovalAll = [...needsApproval, ...approvedThisWeek].sort((a, b) => {
    const order = { pending: 0, rate_limited: 0, approved: 1, scheduled: 1 };
    return (order[a.status] ?? 1) - (order[b.status] ?? 1);
  });

  const totalWeek = needsApproval.length + approvedThisWeek.length;
  const allDone = totalWeek > 0 && needsApproval.length === 0;
  const progressPct = totalWeek > 0 ? Math.round((approvedThisWeek.length / totalWeek) * 100) : 0;

  // Published posts — group by period
  const getPublishedPosts = () => {
    if (pastPosts.length === 0) return [];
    const thisWeekPosts = pastPosts.filter(p => {
      const d = new Date(p.posted_at || p.date || 0);
      return d >= weekStart && d < weekEnd;
    });
    if (publishedPeriod === "this_week") return thisWeekPosts;

    const lastWeekStart = new Date(weekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    if (publishedPeriod === "last_week") {
      return pastPosts.filter(p => {
        const d = new Date(p.posted_at || p.date || 0);
        return d >= lastWeekStart && d < weekStart;
      });
    }

    // Monthly: publishedPeriod = "2026-03" etc
    return pastPosts.filter(p => {
      const d = new Date(p.posted_at || p.date || 0);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      return key === publishedPeriod;
    });
  };

  // Build period options
  const periodOptions = [
    { value: "this_week", label: "This week" },
    { value: "last_week", label: "Last week" },
  ];
  const monthsSeen = new Set();
  pastPosts.forEach(p => {
    const d = new Date(p.posted_at || p.date || 0);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!monthsSeen.has(key)) {
      monthsSeen.add(key);
      const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
      periodOptions.push({ value: key, label });
    }
  });

  const publishedFiltered = getPublishedPosts();
  const [publishedShowAll, setPublishedShowAll] = useState(false);
  const publishedVisible = publishedShowAll ? publishedFiltered : publishedFiltered.slice(0, 20);

  // Shared card props
  const cardProps = {
    user, isAutopilot,
    editingCard, setEditingCard, editSaving, saveCaption,
    postEditComplianceResult, setPostEditComplianceResult,
    swapPanelPostId, setSwapPanelPostId, swapAlternatives, swapLoading,
    swapJustDone, setSwapJustDone, swappingPostId,
    rewriteUsage, openSwapPanel, doSwapArticle, saveSwapLinkStyle,
    handleApprovePost, handleDashRewrite,
    reschedulingCard, setReschedulingCard,
    deletingPost, setDeletingPost, setThisWeek, setToast,
    menuOpen, setMenuOpen, approvingId, setApprovingId,
    impressionsPerPost, isPlug,
  };

  return (
    <div className="fade-up" style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 22, fontWeight: 500 }}>Posts</div>
        {handlePostingMode && (
          <div style={{ display: "flex", gap: 6 }}>
            {[
              { id: "review", label: "📋 Edit First" },
              { id: "autopilot", label: "🤖 Autopilot" },
            ].map(m => (
              <button key={m.id} onClick={() => handlePostingMode(m.id)}
                style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${postingMode === m.id ? G : BORDER}`, background: postingMode === m.id ? "#EBF5FB" : "#fff", cursor: "pointer", fontSize: 13, fontWeight: postingMode === m.id ? 600 : 400, color: postingMode === m.id ? G : "#666", fontFamily: "inherit" }}>
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Week label + progress */}
      <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "16px 20px", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: "#191919" }}>{weekLabel}</span>
          <span style={{ fontSize: 13, color: allDone ? GRN : "#666" }}>
            {postsLoading ? "" : allDone
              ? "All caught up this week ✓"
              : `${needsApproval.length} pending`}
          </span>
        </div>
        <div style={{ height: 6, background: "#e8e8e4", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ height: "100%", background: allDone ? GRN : G, borderRadius: 3, width: `${progressPct}%`, transition: "width 0.3s ease-out" }} />
        </div>
        <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>
          {approvedThisWeek.length} of {totalWeek} approved
        </div>
      </div>

      {/* Autopilot banner */}
      {isAutopilot && (
        <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 10, padding: "14px 18px", marginBottom: 16, fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
          Autopilot is on — posts go out automatically. Switch to Edit First to make changes.
        </div>
      )}

      {/* Loading / Empty */}
      {postsLoading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading…</div>
      ) : totalWeek === 0 && upcoming.length === 0 ? (
        <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "32px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: "#191919", marginBottom: 4 }}>Your first posts are being prepared.</div>
          <div style={{ fontSize: 13, color: "#888" }}>Check back Monday morning.</div>
        </div>
      ) : (
        <>
          {/* Review by deadline banner */}
          {postingMode === "review" && reviewDeadline && !allDone && (
            <div style={{ background: "#EEF6FF", border: "1px solid #bdd6f5", borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#0a66c2", lineHeight: 1.5, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
              <strong>Edit by {new Date(reviewDeadline).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</strong> — posts go out at their scheduled time. Edit, swap, or remove before then.
            </div>
          )}

          {/* ── SECTION 1: NEEDS APPROVAL ── */}
          <Section
            title="Needs approval"
            count={allDone ? "All caught up this week ✓" : `${needsApproval.length} pending`}
            countColor={allDone ? GRN : AMBER}
            defaultOpen={true}
          >
            {needsApprovalAll.length === 0 ? (
              <div style={{ padding: "16px 0", fontSize: 13, color: "#888" }}>No posts this week yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {needsApprovalAll.map(post => (
                  <FullPostCard key={post.id} post={post} {...cardProps} />
                ))}
              </div>
            )}

            {allDone && (
              <div style={{ background: "#E1F5EE", border: "1px solid #A8DCCA", borderRadius: 12, padding: "20px 24px", marginTop: 12, textAlign: "center" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#0F6E56", marginBottom: 4 }}>All done. Your posts go out this week.</div>
                <div style={{ fontSize: 12, color: "#0F6E56" }}>You can still edit approved posts up to 5 minutes before they go out.</div>
              </div>
            )}
          </Section>

          {/* ── SECTION 2: UPCOMING ── */}
          {upcoming.length > 0 && (
            <Section
              title="Upcoming"
              count={`${upcoming.length} upcoming post${upcoming.length !== 1 ? "s" : ""}`}
              defaultOpen={false}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {upcoming.map(post => (
                  expandedUpcoming === post.id ? (
                    <div key={post.id}>
                      <FullPostCard post={post} {...cardProps} />
                      <button onClick={() => setExpandedUpcoming(null)} style={{ background: "transparent", border: "none", color: "#888", fontSize: 12, cursor: "pointer", padding: "4px 0" }}>Collapse</button>
                    </div>
                  ) : (
                    <CompactPostRow
                      key={post.id}
                      post={post}
                      impressionsPerPost={impressionsPerPost}
                      showStatus={true}
                      onClick={() => setExpandedUpcoming(post.id)}
                    />
                  )
                ))}
              </div>
            </Section>
          )}

          {/* ── SECTION 3: PUBLISHED ── */}
          {pastPosts.length > 0 && (
            <Section
              title="Published"
              count={`${pastPosts.length} post${pastPosts.length !== 1 ? "s" : ""}`}
              defaultOpen={false}
            >
              <div style={{ marginBottom: 12 }}>
                <select
                  value={publishedPeriod}
                  onChange={e => { setPublishedPeriod(e.target.value); setPublishedShowAll(false); }}
                  style={{ padding: "8px 12px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 13, fontFamily: "inherit" }}
                >
                  {periodOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {publishedVisible.length === 0 ? (
                <div style={{ padding: "16px 0", fontSize: 13, color: "#888" }}>No published posts in this period.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {publishedVisible.map(post => (
                    <CompactPostRow
                      key={post.id}
                      post={post}
                      impressionsPerPost={impressionsPerPost}
                      showStatus={false}
                    />
                  ))}
                </div>
              )}

              {publishedFiltered.length > 20 && !publishedShowAll && (
                <button onClick={() => setPublishedShowAll(true)} style={{ background: "transparent", color: G, border: "none", fontSize: 13, cursor: "pointer", padding: "8px 0", fontFamily: "inherit" }}>
                  Show more ({publishedFiltered.length - 20} remaining)
                </button>
              )}
            </Section>
          )}
        </>
      )}

      {/* Create post button */}
      {canCreatePost && (
        <div style={{ marginTop: 20, textAlign: "center" }}>
          <button onClick={onShowComposer}
            style={{ background: "transparent", color: G, border: `1px solid ${G}`, borderRadius: 6, padding: "8px 18px", fontSize: 13, cursor: "pointer" }}>
            + Create custom post
          </button>
        </div>
      )}
    </div>
  );
}
